import { nowAsWallClockMs } from './eventDate';

type GigDateLike = {
  end_datetime?: string | null;
  start_datetime?: string | null;
  status?: string | null;
  timezone?: string | null;
  application_start_datetime?: string | null;
  application_end_datetime?: string | null;
  can_apply?: boolean | string | number | null;
  canApply?: boolean | string | number | null;
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Gig times are stored "as picked" (UTC-stamped), so "now" is expressed as the
 * current wall-clock time in the gig's timezone on that same scale.
 */
function toTimestamp(value: Date | number, timeZone?: string | null): number {
  const real =
    value instanceof Date ? value : new Date(Number.isFinite(value) ? value : Date.now());
  return nowAsWallClockMs(timeZone ?? undefined, real);
}

function parseGigDate(value: string, mode: 'start' | 'end'): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    // Date-only strings are day boundaries on the same "as picked" UTC-stamped scale.
    const suffix = mode === 'end' ? 'T23:59:59.999Z' : 'T00:00:00.000Z';
    const parsedDay = new Date(`${trimmed}${suffix}`);
    return Number.isNaN(parsedDay.getTime()) ? null : parsedDay;
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isGigActive(
  gig: GigDateLike | null | undefined,
  now: Date | number = new Date(),
): boolean {
  if (!gig) return false;

  // Check if status explicitly indicates the gig is no longer active
  const status = gig.status?.trim().toLowerCase();
  if (
    status === 'expired' ||
    status === 'closed' ||
    status === 'filled' ||
    status === 'cancelled' ||
    status === 'rejected'
  ) {
    return false;
  }

  const endDatetime = gig.end_datetime?.trim();
  if (!endDatetime) {
    return true;
  }

  const parsedEnd = parseGigDate(endDatetime, 'end');
  if (!parsedEnd) {
    return false;
  }

  const parsedStart = gig.start_datetime?.trim() ? parseGigDate(gig.start_datetime, 'start') : null;
  const nowTs = toTimestamp(now, gig.timezone);

  if (parsedStart && parsedStart.getTime() > nowTs) {
    return true;
  }

  return parsedEnd.getTime() >= nowTs;
}

/**
 * Returns true when applications are currently open for a gig.
 * - If neither application date is set, applications are considered open (no restriction).
 * - If application_start_datetime is set and now is before it, not yet open.
 * - If application_end_datetime is set and now is past it, closed.
 */
export function isApplicationOpen(
  gig: GigDateLike | null | undefined,
  now: Date | number = new Date(),
): boolean {
  if (!gig) return false;
  const nowTs = toTimestamp(now, gig.timezone);

  const appStart = gig.application_start_datetime?.trim();
  const appEnd = gig.application_end_datetime?.trim();

  // No application window set — open by default
  if (!appStart && !appEnd) return true;

  if (appStart) {
    const parsedStart = parseGigDate(appStart, 'start');
    if (parsedStart && nowTs < parsedStart.getTime()) return false; // not yet open
  }

  if (appEnd) {
    const parsedEnd = parseGigDate(appEnd, 'end');
    if (parsedEnd && nowTs > parsedEnd.getTime()) return false; // closed
  }

  return true;
}

/** Returns true when application_start_datetime is set and is still in the future. */
export function isApplicationNotYetOpen(
  gig: GigDateLike | null | undefined,
  now: Date | number = new Date(),
): boolean {
  if (!gig) return false;
  const appStart = gig.application_start_datetime?.trim();
  if (!appStart) return false;
  const parsedStart = parseGigDate(appStart, 'start');
  return !!parsedStart && toTimestamp(now, gig.timezone) < parsedStart.getTime();
}

/**
 * Backend-driven apply eligibility, checked before any client-side validation.
 * Returns true/false when the gig carries `can_apply` (or `canApply`),
 * normalizing boolean/string/number shapes — and null when the field is
 * absent so callers fall back to the date-based checks.
 */
export function readCanApplyFlag(gig: GigDateLike | null | undefined): boolean | null {
  if (!gig) return null;
  const raw = gig.can_apply ?? gig.canApply;
  if (raw == null) return null;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw !== 0;
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 't'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n', 'f', ''].includes(normalized)) return false;
  }
  return null;
}

/**
 * Whether the current user can apply to a gig. The backend `can_apply` flag
 * is authoritative when present; otherwise falls back to the date-based
 * checks. Ownership/visibility guards (`isOwner`, `hideApply`) stay with
 * the callers.
 */
export function canUserApplyToGig(gig: GigDateLike | null | undefined): boolean {
  const flag = readCanApplyFlag(gig);
  if (flag != null) return flag;
  return isGigActive(gig) && isApplicationOpen(gig);
}

export function getGigClosingTimeLabel(
  gig: GigDateLike | null | undefined,
  locale = 'en-US',
): string | null {
  if (!gig) return null;
  const endDatetime = gig.end_datetime?.trim();
  if (!endDatetime) return null;

  const parsedEnd = parseGigDate(endDatetime, 'end');
  if (!parsedEnd) return null;

  // Shown as picked (no conversion); the gig's timezone is only a label.
  const formatted = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  }).format(parsedEnd);

  return `Closes ${formatted}${gig.timezone ? ` (${gig.timezone})` : ''}`;
}

function toTitleCaseStatus(value: string): string {
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export type GigStatusTone = 'positive' | 'negative' | 'neutral';

export type GigStatusPresentation = {
  label: string;
  tone: GigStatusTone;
  isInternal: boolean;
};

export function getGigStatusPresentation(
  gig: GigDateLike | null | undefined,
  isOwner: boolean,
): GigStatusPresentation {
  if (!gig) {
    return {
      label: 'Unknown',
      tone: 'neutral',
      isInternal: false,
    };
  }

  if (!isOwner) {
    const active = isGigActive(gig);
    return {
      label: active ? 'Active' : 'Expired',
      tone: active ? 'positive' : 'negative',
      isInternal: false,
    };
  }

  const normalizedStatus = gig.status?.trim().toLowerCase();
  const label = normalizedStatus ? toTitleCaseStatus(normalizedStatus) : 'Unknown';

  if (
    normalizedStatus === 'published' ||
    normalizedStatus === 'active' ||
    normalizedStatus === 'open'
  ) {
    return { label, tone: 'positive', isInternal: true };
  }

  if (
    normalizedStatus === 'closed' ||
    normalizedStatus === 'filled' ||
    normalizedStatus === 'expired'
  ) {
    return { label, tone: 'negative', isInternal: true };
  }

  return { label, tone: 'neutral', isInternal: true };
}
